import {Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Chip} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useEffect, useState} from "react";
import services, {OneOffEarnPicture} from "../../services";
import {useGlobalModal} from "../../core/GlobalModal.tsx";
import {ONE_OFF_PASS, ONE_OFF_PASS_BENEFITS} from "../../pricing/plans.ts";
import PButton from "../PButton.tsx";
import {suppressSetupTourOnce} from "../events/setupTourFlags.ts";
import GoLivePassModal, {isGoLiveGate} from "./GoLivePassModal.tsx";

export interface EntryCapError {
    code: string; // 'entry_cap' | 'event_pass_required' | 'event_pass_topup_required'
    message?: string;
    cap?: number;
    current?: number;
    has_passes?: boolean;
    /**
     * One-off path gate (OneOffPathService::gatePayload). `path: 'one_off'` switches the modal to
     * the sized pass: `amount_cents` is what Stripe will charge for THIS field, from the same
     * resolver checkout uses, and `earn` is the roster-derived free route.
     */
    path?: 'one_off';
    tournament_uid?: string | null;
    league_uid?: string | null;
    event_type?: 'tournament' | 'league';
    entries?: number;
    counted_entries?: number;
    /** The field the price is spread across in the offer copy — people, or teams for a team sport. */
    headcount?: number;
    headcount_unit?: 'players' | 'teams';
    collects_online?: boolean;
    /** Fees on with a real amount, but Stripe onboarding never finished: the third route applies. */
    stripe_onboarding_incomplete?: boolean;
    free_entries?: number;
    covered_entries?: number | null;
    amount_cents?: number;
    list_cents?: number;
    paid_cents?: number;
    currency?: string;
    entries_covered?: number | null;
    band_label?: string;
    earn?: OneOffEarnPicture;
}

interface EntryCapModalProps {
    data: EntryCapError;
    tournamentUid?: string;
    leagueUid?: string;
    /**
     * Where Stripe returns after buying a pass. Defaults to the current URL, but callers whose page
     * has no stable URL of its own (e.g. the create wizard, which runs as a modal over the dashboard)
     * should pass the created event's manage page so the user lands back where they left off.
     */
    returnPath?: string;
    /** Called after a pass unlocks the event, so the caller can retry the add / refetch. */
    onUnlocked?: () => void;
}

/**
 * Shown when a Pay-as-you-go organizer hits the 8-entry cap adding the 9th player/team. Offers to
 * use an existing Event Pass (unlock this event), buy one, or choose a plan.
 */
export default function EntryCapModal({data, tournamentUid, leagueUid, returnPath, onUnlocked}: EntryCapModalProps) {
    const globalModal = useGlobalModal();
    // Same modal, same buy/use/earn flow — only the framing differs. A pass-required event is
    // not at any limit; it needs a pass because it takes cash or has free entry.
    const passRequired = data.code === 'event_pass_required' || data.code === 'event_pass_topup_required';
    // One-off path: the SIZED pass for this tournament. The gate payload already carries the
    // price from the server's single resolver; status() is re-read below only to refresh it.
    const oneOff = data.path === 'one_off';
    const topup = data.code === 'event_pass_topup_required';
    // A tournament, or a social-format league (Americano, Mexicano, King of the Court) — the
    // gate payload says which; the caller's uid wins when it knows.
    const oneOffTournamentUid = tournamentUid ?? data.tournament_uid ?? undefined;
    const oneOffLeagueUid = oneOffTournamentUid ? undefined : (leagueUid ?? data.league_uid ?? undefined);
    const onboardingIncomplete = !!data.stripe_onboarding_incomplete;
    const [busy, setBusy] = useState<null | 'use' | 'buy' | 'earn'>(null);
    const [price, setPrice] = useState<{amount_cents: number; currency: string} | null>(
        oneOff && data.amount_cents != null && data.currency ? {amount_cents: data.amount_cents, currency: data.currency} : null,
    );
    // First-pass 50% offer (same one the quiz uses) — when set, we show and charge this instead.
    // Never on the one-off path: the sized pass has its promo turned off by config server-side.
    const [promo, setPromo] = useState<{amount_cents: number; currency: string; code: string} | null>(null);

    const cap = data.cap ?? 8;
    const eventKey = tournamentUid ? {tournament_uid: tournamentUid} : {league_uid: leagueUid};

    useEffect(() => {
        services.eventPass.status(oneOff && (oneOffTournamentUid || oneOffLeagueUid) ? {tournament_uid: oneOffTournamentUid, league_uid: oneOffLeagueUid} : undefined)
            .then((s) => {
                if (oneOff) {
                    // effective_price IS the sized quote when the server recognised the tournament.
                    setPrice(s.sized ? s.effective_price : (data.amount_cents != null && data.currency ? {amount_cents: data.amount_cents, currency: data.currency} : s.effective_price));
                    setPromo(null);
                } else {
                    setPrice(s.price);
                    setPromo(s.promo ?? null);
                }
            })
            // The gate payload already priced this; a failed refresh is not worth a toast.
            .catch(() => {});
    }, []);

    const fmt = (p: {amount_cents: number; currency: string} | null) => {
        if (!p) return '';
        try {
            return new Intl.NumberFormat(undefined, {style: 'currency', currency: p.currency}).format(p.amount_cents / 100);
        } catch {
            return `${p.currency.toUpperCase()} ${(p.amount_cents / 100).toFixed(2)}`;
        }
    };
    const basePriceLabel = fmt(price);
    // What this user would actually pay right now.
    const priceLabel = promo ? fmt(promo) : basePriceLabel;

    // Master Admin → Activation reads which route an organizer went for. A measurement only: it
    // never blocks the route, and a route that navigates away waits for it no longer than a blink.
    const trackClick = (action: 'shown' | 'sample' | 'live_example' | 'buy' | 'register_list' | 'register_copy' | 'plan' | 'payment_setup'): Promise<unknown> => {
        if (!oneOff || !(oneOffTournamentUid || oneOffLeagueUid)) return Promise.resolve();
        const sent = services.eventPass.offerClick({tournament_uid: oneOffTournamentUid, league_uid: oneOffLeagueUid, action, amount_cents: price?.amount_cents}).catch(() => null);
        return Promise.race([sent, new Promise((resolve) => setTimeout(resolve, 500))]);
    };
    // The offer opening IS the pass being seen — once per open. The event page's background
    // read of the offer is not, and used to be the only thing counted.
    useEffect(() => { void trackClick('shown'); }, []);

    const usePass = async () => {
        setBusy('use');
        try {
            await services.eventPass.apply(eventKey);
            addToast({color: 'success', title: 'Event Pass applied — this event is now unlimited.'});
            globalModal.close();
            onUnlocked?.();
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not apply the Event Pass.'});
            setBusy(null);
        }
    };

    const buyPass = async () => {
        setBusy('buy');
        void trackClick('buy');
        try {
            const {redirect_url} = oneOff
                ? await services.eventPass.checkout(
                    returnPath ?? (window.location.pathname + window.location.search),
                    undefined,
                    'sized',
                    oneOffLeagueUid,
                    oneOffTournamentUid,
                )
                : await services.eventPass.checkout(
                    returnPath ?? (window.location.pathname + window.location.search),
                    promo?.code,
                    'standard',
                    // The event this pass is for: applied to it the moment it is paid.
                    eventKey.league_uid,
                    eventKey.tournament_uid,
                );
            window.location.href = redirect_url;
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not start checkout.'});
            setBusy(null);
        }
    };

    const choosePlan = async () => {
        globalModal.close();
        await trackClick('plan');
        window.location.href = '/pricing/';
    };

    // The no-money path: pledge to get the players signed up instead. Lifts the entry cap so the
    // full field can be added right away and the draw builds; scoring waits until 9+ entries are signed up
    // (EarnFreePassCard on Setup → Players tracks it). Retries the blocked add, then lands the
    // organizer on that tab.
    const startEarn = async () => {
        setBusy('earn');
        try {
            await services.eventPass.earnStart(eventKey);
            addToast({color: 'success', title: 'Entry limit lifted — invite your players. Scoring unlocks once 9+ have signed up.'});
            globalModal.close();
            onUnlocked?.();
            const path = tournamentUid
                ? `/tournaments/${tournamentUid}/setup/applications/`
                : `/leagues/${leagueUid}/setup/applications/`;
            if (!window.location.pathname.includes('/setup/applications')) {
                suppressSetupTourOnce(); // sent to Players on purpose — the help guide waits
                window.location.href = path;
            }
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not start the free unlock.'});
            setBusy(null);
        }
    };

    // One-off path: the free route is the roster, not a pledge. The place to act is the Players
    // tab, where every entrant's registration state and the share link live.
    const goToPlayers = async () => {
        globalModal.close();
        const tracked = trackClick('register_list');
        // Social formats have no Players tab of their own — their roster lives on the event page.
        const path = oneOffTournamentUid
            ? `/tournaments/${oneOffTournamentUid}/setup/applications/`
            : `/leagues/${oneOffLeagueUid}/setup/applications/`;
        if (!window.location.pathname.includes('/setup/applications')) {
            // The organizer asked to see their players. The Setup help guide auto-starts on a
            // first visit and walks tab to tab — it used to take this landing over and they
            // never saw the Players tab. Postponed, not cancelled.
            await tracked;
            suppressSetupTourOnce();
            window.location.href = path;
        }
    };

    // The third route: the event is set to take a real entry fee online, but the payout account
    // was never completed — so every entry still counts. Finishing setup makes self-registered
    // entries stop counting, and the gate re-evaluates on the way back.
    const [onboardingBusy, setOnboardingBusy] = useState(false);
    const finishPaymentSetup = async () => {
        setOnboardingBusy(true);
        void trackClick('payment_setup');
        try {
            const response: any = await services.payouts.onboardingUrl(window.location.href);
            if (response?.redirect_url) {
                window.location.href = response.redirect_url;
                return;
            }
            addToast({color: 'danger', title: response?.error || 'Could not open payment setup.'});
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not open payment setup.'});
        }
        setOnboardingBusy(false);
    };
    const copyJoinLink = async () => {
        if (!data.earn?.join_link) return;
        void trackClick('register_copy');
        try {
            await navigator.clipboard.writeText(data.earn.join_link);
            addToast({color: 'success', title: 'Registration link copied — drop it in your group chat.'});
        } catch {
            addToast({color: 'warning', title: data.earn.join_link});
        }
    };

    if (oneOff) {
        const earn = data.earn;
        const freeEntries = data.free_entries ?? cap;
        // Esports: Swiss or double elimination was chosen on an event with no pass. The field may be
        // inside the free band, so the entry-count framing is dropped and the offer says what it is.
        const formatOffer = (data as any).reason === 'advanced_formats';
        // "With 18 players, that's just $0.56 each." Rounded UP to the cent: never less than it costs.
        const perHeadLine = price && data.headcount && data.headcount > 0
            ? ONE_OFF_PASS.perHead(data.headcount, data.headcount_unit || 'players', fmt({amount_cents: Math.ceil(price.amount_cents / data.headcount), currency: price.currency}))
            : null;
        const esports = formatOffer || !!(data as any).esports;
        return (
            // scrollBehavior inside: the offer is taller than a phone screen, and the Buy button
            // lives in the footer — the body scrolls, the button stays in reach.
            <Modal isOpen onClose={() => globalModal.close()} placement="center" scrollBehavior="inside" classNames={{wrapper: 'z-[130]', backdrop: 'z-[125]'}}>
                <ModalContent>
                    <ModalHeader className="flex-col items-start gap-1">
                        <span className="flex items-center gap-2"><Icon icon="material-symbols-light:lock-outline" className="text-2xl text-primary" /> {topup ? ONE_OFF_PASS.topupTitle : formatOffer ? ONE_OFF_PASS.formatTitle : ONE_OFF_PASS.liveTitle}</span>
                        {!formatOffer && (
                            <span className="text-xs font-normal text-default-500">
                                {data.entries != null ? `${data.entries} entries · ` : ''}{ONE_OFF_PASS.freeUpTo(freeEntries)}
                            </span>
                        )}
                    </ModalHeader>
                    <ModalBody className="gap-3">
                        <p className="text-sm text-default-600">
                            {topup ? ONE_OFF_PASS.topupBody : formatOffer ? ONE_OFF_PASS.formatBody(priceLabel || null) : ONE_OFF_PASS.liveBody}
                            {/* The price spread across the field — only when the server sent a field and a price. */}
                            {!topup && !formatOffer && perHeadLine && <>{' '}<strong>{perHeadLine}</strong></>}
                            {/* A top-up keeps the online-fees alternative; the first offer leads with the value instead. */}
                            {topup && <>{' '}<strong>{onboardingIncomplete ? ONE_OFF_PASS.onboardingAlternative : ONE_OFF_PASS.onlineAlternative}</strong></>}
                        </p>

                        {/* The third route: payment setup was started but never finished. */}
                        {onboardingIncomplete && (
                            <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 flex items-center gap-3">
                                <Icon icon="material-symbols-light:account-balance-outline" className="text-2xl text-primary shrink-0" />
                                <div className="flex-1 text-sm">
                                    <div className="font-medium">{ONE_OFF_PASS.onboardingTitle}</div>
                                    <div className="text-default-500 text-xs">{ONE_OFF_PASS.onboardingBody}</div>
                                </div>
                                <PButton size="sm" color="primary" variant="flat" isLoading={onboardingBusy} isDisabled={busy !== null} onPress={finishPaymentSetup}>
                                    Finish payment setup
                                </PButton>
                            </div>
                        )}

                        {/* The pass, sized for THIS event, priced by the server's resolver. */}
                        <div className="rounded-lg border-2 border-dashed border-[#E6C200] bg-[#FFFCEC] p-3">
                            <div className="flex items-start gap-3">
                            <Icon icon="material-symbols-light:confirmation-number-outline" className="text-2xl text-[#9a7d00] shrink-0" />
                            {/* min-w-0: a flex child will not shrink below its content without it, and
                                anything wide in here pushed out through the side of the pass box. */}
                            <div className="min-w-0 flex-1 text-sm">
                                <div className="font-semibold flex items-center gap-2 flex-wrap">
                                    {ONE_OFF_PASS.name}{data.band_label ? ` — ${data.band_label}` : ''}
                                    {priceLabel && <span className="text-[#9a7d00]">{priceLabel} one-time</span>}
                                    {topup && <Chip size="sm" color="warning" variant="flat">top-up</Chip>}
                                </div>
                                <div className="text-default-500 text-xs mt-0.5">
                                    {topup && data.paid_cents != null && data.list_cents != null
                                        ? `The next band is ${fmt({amount_cents: data.list_cents, currency: data.currency || 'usd'})}; you've already paid ${fmt({amount_cents: data.paid_cents, currency: data.currency || 'usd'})}, so only the difference is due.`
                                        : ONE_OFF_PASS.what}
                                    {data.entries_covered != null ? ` Covers up to ${data.entries_covered} entries.` : ' Covers any number of entries.'}
                                </div>
                                {/* What the pass buys for this event — copy from plans.ts only. */}
                                {!topup && (
                                    <ul className="mt-2 space-y-0.5 text-xs text-default-600">
                                        {ONE_OFF_PASS_BENEFITS.benefitsFor(esports).map((b) => (
                                            <li key={b} className="flex items-start gap-1.5">
                                                <Icon icon="material-symbols-light:check-circle-outline" className="mt-0.5 shrink-0 text-base text-success" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            </div>
                            {/* The PDFs the pass includes, as samples they can open: a drawn page, not an
                                image, so it costs nothing to load until they click. Full width of the pass
                                box, two equal columns that may shrink (min-w-0) and whose text wraps —
                                they used to sit in the indented text column and push out of the box. */}
                            {!topup && (
                                <div className="mt-3 border-t border-[#E6C200]/40 pt-2.5">
                                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-default-500">{ONE_OFF_PASS_BENEFITS.samplesTitle}</div>
                                    {/* A real public live page first, full width: it is what their players
                                        will follow on the day. A league offer opens the league example. */}
                                    <a href={oneOffLeagueUid ? ONE_OFF_PASS_BENEFITS.liveExample.league : ONE_OFF_PASS_BENEFITS.liveExample.tournament}
                                       target="_blank" rel="noreferrer" onClick={() => { void trackClick('live_example'); }}
                                       className="group mb-2 flex min-w-0 items-center gap-2.5 rounded-lg border border-default-200 bg-content1 p-2 transition hover:border-primary">
                                        <span aria-hidden className="flex h-11 w-16 shrink-0 flex-col justify-center gap-[5px] rounded-[4px] bg-[#0b1437] px-1.5">
                                            <span className="flex items-center gap-1">
                                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-danger" />
                                                <span className="text-[7px] font-bold leading-none tracking-wider text-white/80">LIVE</span>
                                            </span>
                                            <span className="flex items-center justify-between gap-1"><span className="h-[3px] flex-1 rounded bg-white/70" /><span className="text-[8px] font-bold leading-none text-white">2</span></span>
                                            <span className="flex items-center justify-between gap-1"><span className="h-[3px] flex-1 rounded bg-white/35" /><span className="text-[8px] font-bold leading-none text-white/70">1</span></span>
                                        </span>
                                        <span className="min-w-0 flex-1 leading-tight">
                                            <span className="block break-words text-xs font-semibold group-hover:text-primary">{ONE_OFF_PASS_BENEFITS.liveExample.label}</span>
                                            <span className="block break-words text-[11px] text-default-500">{ONE_OFF_PASS_BENEFITS.liveExample.hint}</span>
                                        </span>
                                        <span className="shrink-0 whitespace-nowrap text-[11px] font-medium text-primary">{ONE_OFF_PASS_BENEFITS.liveExample.cta}</span>
                                    </a>
                                    <div className="grid grid-cols-2 gap-2">
                                        {ONE_OFF_PASS_BENEFITS.samples.map((s) => (
                                            <a key={s.key} href={s.href} target="_blank" rel="noreferrer" onClick={() => { void trackClick('sample'); }}
                                               className="group flex min-w-0 items-center gap-2 rounded-lg border border-default-200 bg-content1 p-2 transition hover:border-primary">
                                                <span aria-hidden className="flex h-11 w-8 shrink-0 flex-col gap-[3px] rounded-[3px] border border-default-300 bg-white p-1 shadow-sm">
                                                    <span className="h-1.5 w-full rounded-[1px] bg-primary" />
                                                    <span className="h-[2px] w-4/5 rounded bg-default-300" />
                                                    <span className="h-[2px] w-full rounded bg-default-200" />
                                                    <span className="h-[2px] w-full rounded bg-default-200" />
                                                    <span className="h-[2px] w-3/5 rounded bg-default-200" />
                                                    <span className="mt-auto h-[3px] w-full rounded bg-default-200" />
                                                </span>
                                                <span className="min-w-0 flex-1 leading-tight">
                                                    <span className="block break-words text-xs font-semibold group-hover:text-primary">{s.label}</span>
                                                    <span className="block break-words text-[11px] text-default-500">{s.hint}</span>
                                                    <span className="mt-0.5 block text-[11px] font-medium text-primary">View sample</span>
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                    {/* What takes the risk out of buying — commitments, worded in plans.ts. */}
                                    <ul className="mt-2.5 space-y-1 text-xs text-default-600">
                                        {ONE_OFF_PASS_BENEFITS.reassurances.map((r) => (
                                            <li key={r.key} className="flex items-start gap-1.5">
                                                <Icon icon={r.icon} className="mt-px shrink-0 text-base text-[#9a7d00]" />
                                                <span>{r.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* The no-money route: the roster. Hidden for team sports, where there is nobody to register. */}
                        {earn && earn.applies && (
                            <div className="rounded-lg border border-success-200 bg-success-50/50 dark:bg-success-900/10 p-3 space-y-2">
                                <div className="flex items-start gap-3">
                                    <Icon icon="material-symbols-light:redeem" className="text-2xl text-success shrink-0" />
                                    <div className="text-sm">
                                        <div className="font-semibold flex items-center gap-2 flex-wrap">
                                            {ONE_OFF_PASS.earnTitle}
                                            <Chip size="sm" color="success" variant="flat">{earn.claimed} of {earn.required} registered</Chip>
                                        </div>
                                        <div className="text-default-500 text-xs mt-0.5">
                                            {ONE_OFF_PASS.earnBody(earn.required, earn.entrants)}
                                            {!earn.eligible && ` (Needs at least ${earn.min_entrants} players entered.)`}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <PButton size="sm" color="success" variant="flat" className="flex-1"
                                             isDisabled={busy !== null} onPress={goToPlayers}>
                                        See who still needs to register
                                    </PButton>
                                    {earn.join_link && (
                                        <PButton size="sm" variant="bordered" isDisabled={busy !== null} onPress={copyJoinLink}
                                                 startContent={<Icon icon="material-symbols-light:link" className="text-lg" />}>
                                            Copy registration link
                                        </PButton>
                                    )}
                                </div>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter className="flex-wrap gap-2">
                        <PButton variant="light" onPress={choosePlan}>Or choose a plan</PButton>
                        <PButton color="primary" onPress={buyPass} isLoading={busy === 'buy'} isDisabled={!price}
                                 startContent={<Icon icon="material-symbols-light:confirmation-number-outline" className="text-lg" />}>
                            {topup ? 'Pay the difference' : 'Buy the Event Pass'}{priceLabel ? ` — ${priceLabel}` : ''}
                        </PButton>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal isOpen onClose={() => globalModal.close()} placement="center" classNames={{wrapper: 'z-[130]', backdrop: 'z-[125]'}}>
            <ModalContent>
                <ModalHeader className="flex-col items-start gap-1">
                    <span className="flex items-center gap-2"><Icon icon="material-symbols-light:lock-outline" className="text-2xl text-primary" /> {passRequired ? 'This event needs an Event Pass' : 'Free event limit reached'}</span>
                    <span className="text-xs font-normal text-default-500">{passRequired ? "It's set to cash or free entry." : `This free event is limited to ${cap} players or teams.`}</span>
                </ModalHeader>
                <ModalBody className="gap-3">
                    {passRequired ? (
                        /* Lead with the FREE route. An organizer pushed towards cash is worth nothing to us —
                           the incentive has to point at online payment, so that option comes first. */
                        <p className="text-sm text-default-600">
                            This event needs an Event Pass because it's cash or free entry.
                            {' '}<strong>Switch it to online payment and it's free</strong> — we take our fee from
                            {' '}registrations instead.
                        </p>
                    ) : (
                    <p className="text-sm text-default-600">
                        You've reached {cap} entries on this event. Unlock <strong>this event</strong> with an Event Pass, or
                        choose a plan for unlimited entries on every event. Connecting Stripe to take online payments also
                        removes the limit.
                    </p>
                    )}

                    {/* What an Event Pass is + its cost */}
                    <div className="rounded-lg border-2 border-dashed border-[#E6C200] bg-[#FFFCEC] p-3 flex items-start gap-3">
                        <Icon icon="material-symbols-light:confirmation-number-outline" className="text-2xl text-[#9a7d00] shrink-0" />
                        <div className="text-sm">
                            <div className="font-semibold flex items-center gap-2 flex-wrap">
                                Event Pass
                                {promo && priceLabel ? (
                                    <>
                                        <span className="text-[#9a7d00]">
                                            <s className="opacity-60 mr-1">{basePriceLabel}</s>{priceLabel} one-time
                                        </span>
                                        <Chip size="sm" color="warning" variant="flat">50% off — special offer</Chip>
                                    </>
                                ) : (
                                    priceLabel && <span className="text-[#9a7d00]">{priceLabel} one-time</span>
                                )}
                            </div>
                            <div className="text-default-500 text-xs mt-0.5">
                                Unlocks <strong>unlimited players or teams for this one offline event</strong>. It's a single-use
                                purchase — your other events stay on the free {cap}-entry limit.
                            </div>
                        </div>
                    </div>

                    {/* The no-money path: pledge to get the players signed up instead. Racquet sports
                        only — the server says so (earn_applies); absent on older payloads = shown. */}
                    {(data as any).earn_applies !== false && <div className="rounded-lg border border-success-200 bg-success-50/50 dark:bg-success-900/10 p-3 space-y-2">
                        <div className="flex items-start gap-3">
                            <Icon icon="material-symbols-light:redeem" className="text-2xl text-success shrink-0" />
                            <div className="text-sm">
                                <div className="font-semibold">Or get your players signed up — free</div>
                                <div className="text-default-500 text-xs mt-0.5">
                                    Add your full field now and invite everyone — by email or your event link. Once
                                    9+ entries are signed up, this event unlocks with unlimited players or teams and
                                    scores can be recorded. Your draw builds either way.
                                </div>
                            </div>
                        </div>
                        <PButton size="sm" color="success" variant="flat" className="w-full"
                                 isLoading={busy === 'earn'} isDisabled={busy === 'buy' || busy === 'use'}
                                 onPress={startEarn}>
                            Get players signed up
                        </PButton>
                    </div>}

                    {data.has_passes && (
                        <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 flex items-center gap-3">
                            <Icon icon="material-symbols-light:confirmation-number-outline" className="text-2xl text-primary shrink-0" />
                            <div className="flex-1 text-sm">
                                <div className="font-medium">You have an Event Pass available</div>
                                <div className="text-default-500 text-xs">Apply it to unlock unlimited entries for this event.</div>
                            </div>
                            <Chip size="sm" color="primary" variant="flat">Ready</Chip>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="flex-wrap gap-2">
                    <PButton variant="light" onPress={choosePlan}>Choose a plan</PButton>
                    {data.has_passes ? (
                        <>
                            <PButton variant="flat" onPress={buyPass} isLoading={busy === 'buy'}>Buy another{priceLabel ? ` (${priceLabel})` : ''}</PButton>
                            <PButton color="primary" onPress={usePass} isLoading={busy === 'use'}
                                     startContent={<Icon icon="material-symbols-light:lock-open-outline" className="text-lg" />}>
                                Use my Event Pass
                            </PButton>
                        </>
                    ) : (
                        <PButton color="primary" onPress={buyPass} isLoading={busy === 'buy'}
                                 startContent={<Icon icon="material-symbols-light:confirmation-number-outline" className="text-lg" />}>
                            Buy an Event Pass{priceLabel ? ` — ${priceLabel}` : ''}
                        </PButton>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/**
 * If `err` is the backend entry-cap error, open the gate modal and return true (handled). Callers
 * put this first in their add-mutation error handler and skip their default toast when it returns
 * true. `err` may be a rejected object (apiActionImproved) or a resolved {error, code} (apiAction).
 */
export function handleEntryCapError(
    globalModal: ReturnType<typeof useGlobalModal>,
    err: any,
    ctx: {tournamentUid?: string; leagueUid?: string; returnPath?: string; onUnlocked?: () => void},
): boolean {
    // Both codes open this modal: the 8-entry cap and the cash/free-entry pass gate are answered
    // by the same buy / use / earn flow. Broadened here rather than at each call site so every
    // existing caller — the quiz, both applications tabs — picks up the new code for free.
    if (!err || (err.code !== 'entry_cap' && err.code !== 'event_pass_required' && err.code !== 'event_pass_topup_required')) return false;
    globalModal.open(isGoLiveGate(err)
        ? <GoLivePassModal data={err} tournamentUid={ctx.tournamentUid} leagueUid={ctx.leagueUid} returnPath={ctx.returnPath} onUnlocked={ctx.onUnlocked} />
        : <EntryCapModal data={err} tournamentUid={ctx.tournamentUid} leagueUid={ctx.leagueUid} returnPath={ctx.returnPath} onUnlocked={ctx.onUnlocked} />);
    return true;
}
